import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { createApiUrlWithPrefix, getUserImageUrl } from '../config/api';
import { toast } from 'react-hot-toast';
import { useUserCounts } from '../contexts/UserCountsContext';
import {
  Search,
  UserPlus,
  Users2,
  Edit,
  Trash2,
  RefreshCw,
  Phone,
  Calendar,
  Eye,
  Printer,
  Download,
  ChevronLeft,
  ChevronRight,
  CreditCard
} from 'lucide-react';
import UserCreationModal from './UserCreationModal';
import UserEditModal from './UserEditModal';
import UserDetailsModal from './UserDetailsModal';
import Subscribers from './Subscribers';

import { API_BASE_URL } from '../config/api';
function Users() {
  const { t, i18n } = useTranslation();
  const { userCounts } = useUserCounts();
  const isRTL = i18n.language === 'ar';

  // State management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [subscriptionStatuses, setSubscriptionStatuses] = useState([]);
  const [selectedRoleForCreation, setSelectedRoleForCreation] = useState('Rider');
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'subscribers'
  const [ageGroupFilter, setAgeGroupFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' = All, '0' = Active, '1' = Deactivated
  const [activeCount, setActiveCount] = useState(0);
  const [deactivatedCount, setDeactivatedCount] = useState(0);
  const [sortOrder, setSortOrder] = useState('name'); // 'name', 'age-asc', 'age-desc'

  const [refreshKey, setRefreshKey] = useState(0);
  // Fetch subscription statuses
  const fetchSubscriptionStatuses = useCallback(async () => {
    try {
      const response = await axios.get(createApiUrlWithPrefix('subscriptions/status'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setSubscriptionStatuses(response.data);
    } catch (error) {
      console.error('Error fetching subscription statuses:', error);
    }
  }, []);

  // Fetch users from backend
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        ...(searchTerm && { search: searchTerm }),
        ...(roleFilter && { role: roleFilter }),
        ...(statusFilter !== '' && { status: statusFilter })
      });

      const response = await axios.get(createApiUrlWithPrefix(`users?${params}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setUsers(response.data.users || response.data);
      setTotalPages(response.data.totalPages || 1);
      setTotalItems(response.data.totalItems || response.data.length);
      if (response.data.activeCount !== undefined) {
        setActiveCount(response.data.activeCount);
        setDeactivatedCount(response.data.deactivatedCount);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm, roleFilter, statusFilter]);

  useEffect(() => {
    fetchUsers();
    fetchSubscriptionStatuses();
  }, [fetchUsers, fetchSubscriptionStatuses]);

  // Helper function to get subscription status for a user
  const getUserSubscriptionStatus = (userId) => {
    return subscriptionStatuses.find(status => status.userId === userId);
  };

  // Helper function to get subscription status color
  const getSubscriptionStatusColor = (status) => {
    switch (status) {
      case 'Paid':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Overdue':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'No Subscription':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Event handlers
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleRoleFilter = (e) => {
    setRoleFilter(e.target.value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setRoleFilter('');
    setAgeGroupFilter('');
    setStatusFilter('');
    setSortOrder('name');
    setCurrentPage(1);
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user.id));
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowEditModal(true);
  };

  const handleViewUserDetails = (user) => {
    setViewingUser(user);
    setShowDetailsModal(true);
  };

  const handleUserCreated = async () => {
    console.log('🔄 User created, refreshing list...');
    await fetchUsers(); // Refresh the list
    setRefreshKey(prev => prev + 1); // Force component refresh
    console.log('✅ Users list refreshed after creation');
  };

  const handleUserUpdated = () => {
    fetchUsers();
    setShowEditModal(false);
    setEditingUser(null);
  };

  // Print functionality
  const handlePrintUsers = () => {
    const printWindow = window.open('', '_blank');
    const usersToPrint = users.filter(user => selectedUsers.length > 0 ? selectedUsers.includes(user.id) : true);

    printWindow.document.write(`
      <html>
        <head>
          <title>Users Report - ${new Date().toLocaleDateString()}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
            .user-card { 
              border: 1px solid #ddd; 
              margin: 15px 0; 
              padding: 20px; 
              border-radius: 8px; 
              page-break-inside: avoid;
              display: flex;
              align-items: center;
            }
            .user-photo { 
              width: 80px; 
              height: 80px; 
              border-radius: 50%; 
              margin-right: 20px; 
              background: #f0f0f0;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 24px;
              font-weight: bold;
              color: #666;
            }
            .user-info { flex: 1; }
            .user-name { font-size: 18px; font-weight: bold; margin-bottom: 5px; }
            .user-details { color: #666; margin: 3px 0; }
            .role-badge { 
              background: #e3f2fd; 
              color: #1976d2; 
              padding: 4px 8px; 
              border-radius: 12px; 
              font-size: 12px; 
              font-weight: bold;
            }
            .status-badge { 
              background: #e8f5e8; 
              color: #2e7d32; 
              padding: 4px 8px; 
              border-radius: 12px; 
              font-size: 12px; 
              font-weight: bold;
            }
            @media print {
              body { margin: 0; }
              .user-card { margin: 10px 0; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Equestrian Club - Users Report</h1>
            <p>Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
            <p>Total Users: ${usersToPrint.length}</p>
          </div>
          ${usersToPrint.map(user => `
            <div class="user-card">
              <div class="user-photo">
                ${user.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div class="user-info">
                <div class="user-name">${user.name || 'Unknown User'}</div>
                <div class="user-details">Email: ${user.email || 'N/A'}</div>
                <div class="user-details">Username: @${user.username || 'N/A'}</div>
                <div class="user-details">Phone: ${user.phoneNumber || 'N/A'}</div>
                <div class="user-details">Age: ${user.birthDate && user.birthDate !== '0001-01-01T00:00:00' ? new Date().getFullYear() - new Date(user.birthDate).getFullYear() : 'N/A'}</div>
                <div class="user-details">Address: ${user.address || 'N/A'}</div>
                <div class="user-details">Blood Type: ${user.bloodType || 'N/A'}</div>
                ${user.role === 0 ? `<div class="user-details">Level: ${getRiderLevelName(user.level)}</div>` : ''}
                <div style="margin-top: 10px;">
                  <span class="role-badge">${getRoleName(user.role)}</span>
                  <span class="status-badge">Active</span>
                </div>
              </div>
            </div>
          `).join('')}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.print();
  };

  const handleExportUsers = () => {
    const usersToExport = users.filter(user => selectedUsers.length > 0 ? selectedUsers.includes(user.id) : true);
    const csvContent = [
      ['Name', 'Email', 'Username', 'Phone', 'Age', 'Address', 'Blood Type', 'Role', 'Level', 'Status'],
      ...usersToExport.map(user => [
        user.name || 'Unknown User',
        user.email || 'N/A',
        user.username || 'N/A',
        user.phoneNumber || 'N/A',
        user.birthDate && user.birthDate !== '0001-01-01T00:00:00' ? new Date().getFullYear() - new Date(user.birthDate).getFullYear() : 'N/A',
        user.address || 'N/A',
        user.bloodType || 'N/A',
        getRoleName(user.role),
        user.role === 0 ? getRiderLevelName(user.level) : 'N/A',
        'Active'
      ])
    ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print badge functionality
  const handlePrintBadge = async (user) => {
    try {
      const response = await axios.get(createApiUrlWithPrefix(`users/${user.id}/qr-code`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const qrData = response.data.userGuid || response.data.qrCode || `EC_${user.id}`;

      // Debug logging
      console.log('User data for badge:', {
        firstName: user.firstName,
        lastName: user.lastName,
        birthDate: user.birthDate,
        bloodType: user.bloodType,
        qrData: qrData
      });

      // Format birth date
      const birthDate = user.birthDate && user.birthDate !== null
        ? new Date(user.birthDate).toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        })
        : 'Not provided';

      // Create badge HTML for printing
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Badge - ${user.firstName} ${user.lastName}</title>
            <style>
              body { 
                font-family: 'Segoe UI', Arial, sans-serif; 
                margin: 0; 
                padding: 20px; 
                background: white;
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
              }
              .badge { 
                width: 500px; 
                height: 420px; 
                border: 2px solid #2c3e50; 
                margin: 0 auto;
                background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                border-radius: 15px;
                box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                position: relative;
                overflow: hidden;
              }
              .header { 
                background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                color: white;
                padding: 15px 25px;
                text-align: center;
                font-weight: bold;
                font-size: 18px;
                letter-spacing: 1px;
              }
              .content { 
                padding: 25px;
                display: flex;
                gap: 20px;
                height: calc(100% - 70px);
              }
              .left-section { 
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 10px;
              }
              .user-photo { 
                width: 100px; 
                height: 100px; 
                border-radius: 50%; 
                border: 3px solid #3b82f6;
                object-fit: cover;
                margin-bottom: 15px;
              }
              .user-photo-placeholder { 
                width: 100px; 
                height: 100px; 
                border-radius: 50%; 
                border: 3px solid #3b82f6;
                background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: bold;
                font-size: 32px;
                color: #3b82f6;
                margin-bottom: 15px;
              }
              .user-name { 
                font-size: 22px; 
                font-weight: bold; 
                color: #2c3e50;
                margin-bottom: 8px;
              }
              .user-details { 
                font-size: 14px; 
                color: #5a6c7d;
                line-height: 1.6;
              }
              .detail-item { 
                margin-bottom: 8px;
                display: flex;
                align-items: center;
                gap: 8px;
              }
              .right-section { 
                flex: 1;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
              }
              .qr-container { 
                width: 140px; 
                height: 140px; 
                border: 2px solid #3b82f6;
                border-radius: 10px;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                background: white;
                margin-bottom: 15px;
              }
              .qr-code { 
                width: 120px;
                height: 120px;
                display: flex;
                align-items: center;
                justify-content: center;
              }
              .qr-label { 
                font-size: 12px; 
                color: #3b82f6;
                font-weight: bold;
                text-align: center;
                margin-bottom: 10px;
              }
              .role-badge { 
                background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                color: white;
                padding: 6px 16px;
                border-radius: 20px;
                font-size: 12px;
                font-weight: bold;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              }
              @media print {
                body { margin: 0; padding: 10px; }
                .badge { margin: 0; box-shadow: none; }
              }
            </style>
          </head>
          <body>
            <div class="badge">
              <div class="header">EQUESTRIAN CLUB MEMBER</div>
              <div class="content">
                <div class="left-section">
                  ${(user.photoPath || user.imagePath) ?
          `<img src="${API_BASE_URL}/${user.photoPath || user.imagePath}" alt="User Photo" class="user-photo" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />` :
          ''
        }
                  <div class="user-photo-placeholder" style="${(user.photoPath || user.imagePath) ? 'display: none;' : 'display: flex;'}">
                    ${user.firstName?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  
                  <div class="user-name">${user.firstName} ${user.lastName}</div>
                  <div class="user-details">
                    <div class="detail-item">📅 Birth Date: ${birthDate}</div>
                    <div class="detail-item">🩸 Blood Type: ${user.bloodType || 'N/A'}</div>
                  </div>
                </div>
                
                <div class="right-section">
                  <div class="qr-container">
                    <div class="qr-code" id="qr-code-${user.id}"></div>
                  </div>
                  <div class="qr-label">SCAN QR CODE</div>
                  <div class="role-badge">${getRoleName(user.role)}</div>
                </div>
              </div>
            </div>
            <script src="https://cdn.jsdelivr.net/npm/qrcode@1.5.3/build/qrcode.min.js"></script>
            <script>
              // Generate QR code after page loads
              function generateQRCode() {
                const qrElement = document.getElementById('qr-code-${user.id}');
                console.log('QR Element found:', qrElement);
                console.log('QRCode library available:', typeof QRCode !== 'undefined');
                console.log('QR Data:', '${qrData}');
                
                if (qrElement && typeof QRCode !== 'undefined') {
                  try {
                    // Create a canvas element
                    const canvas = document.createElement('canvas');
                    qrElement.appendChild(canvas);
                    
                    QRCode.toCanvas(canvas, '${qrData}', {
                      width: 120,
                      height: 120,
                      margin: 1,
                      color: { 
                        dark: '#2c3e50', 
                        light: '#ffffff' 
                      },
                      errorCorrectionLevel: 'M'
                    }, function (error) {
                      if (error) {
                        console.error('QR Code generation failed:', error);
                        qrElement.innerHTML = '<div style="color: #2c3e50; font-size: 10px; text-align: center; padding: 10px; background: #f0f0f0; border: 1px solid #ccc; border-radius: 5px;">QR CODE<br/>${qrData}</div>';
                      } else {
                        console.log('QR Code generated successfully');
                        // Make sure the canvas is visible
                        canvas.style.display = 'block';
                        canvas.style.margin = '0 auto';
                      }
                    });
                  } catch (err) {
                    console.error('QR Code generation error:', err);
                    qrElement.innerHTML = '<div style="color: #2c3e50; font-size: 10px; text-align: center; padding: 10px; background: #f0f0f0; border: 1px solid #ccc; border-radius: 5px;">QR CODE<br/>${qrData}</div>';
                  }
                } else {
                  console.error('QR Element not found or QRCode library not loaded');
                  if (qrElement) {
                    qrElement.innerHTML = '<div style="color: #2c3e50; font-size: 10px; text-align: center; padding: 10px; background: #f0f0f0; border: 1px solid #ccc; border-radius: 5px;">QR CODE<br/>${qrData}</div>';
                  }
                }
              }
              
              // Try multiple times to ensure QRCode library is loaded
              let attempts = 0;
              const maxAttempts = 10;
              
              function tryGenerateQR() {
                if (typeof QRCode !== 'undefined') {
                  generateQRCode();
                } else if (attempts < maxAttempts) {
                  attempts++;
                  setTimeout(tryGenerateQR, 200);
                } else {
                  console.error('QRCode library failed to load after multiple attempts');
                  const qrElement = document.getElementById('qr-code-${user.id}');
                  if (qrElement) {
                    qrElement.innerHTML = '<div style="color: #2c3e50; font-size: 10px; text-align: center; padding: 10px; background: #f0f0f0; border: 1px solid #ccc; border-radius: 5px;">QR CODE<br/>${qrData}</div>';
                  }
                }
              }
              
              // Start trying to generate QR code
              tryGenerateQR();
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
      toast.success('Badge opened for printing');
    } catch (error) {
      console.error('Failed to print badge:', error);
      toast.error('Failed to print badge');
    }
  };


  // Helper functions
  const getRoleName = (role) => {
    switch (role) {
      case 4: return 'SuperAdmin';
      case 3: return 'Admin';
      case 2: return 'Coach';
      case 1: return 'Owner';
      case 0: return 'Rider';
      case 5: return 'Temporary';
      default: return 'Unknown';
    }
  };

  const getRiderLevelName = (level) => {
    switch (level) {
      case 0: return 'Beginner';
      case 1: return 'Intermediate';
      case 2: return 'Professional';
      default: return 'Unknown';
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 4: return 'bg-red-100 text-red-800';
      case 3: return 'bg-purple-100 text-purple-800';
      case 2: return 'bg-blue-100 text-blue-800';
      case 1: return 'bg-green-100 text-green-800';
      case 0: return 'bg-yellow-100 text-yellow-800';
      case 5: return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Removed early return for loading to prevent search bar unmounting

  return (
    <>
      <div className="flex flex-col min-h-full bg-neutral-50 pb-8" dir={isRTL ? 'rtl' : 'ltr'} key={refreshKey}>
        <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6">
          {/* Header - Responsive */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('users.title')}</h1>
              <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">
                {t('users.subtitle')}
              </p>
            </div>
          </div>

          {/* Premium Segmented Control Tabs */}
          <div className="bg-gray-100/80 p-1.5 rounded-2xl flex space-x-2 max-w-md mx-auto sm:mx-0 shadow-inner">
            <button
              onClick={() => setActiveTab('users')}
              className={`flex-1 flex items-center justify-center px-6 py-3 rounded-xl text-sm font-bold transition-all duration-300 ${
                activeTab === 'users'
                  ? 'bg-white text-primary-600 shadow-md transform scale-[1.02] ring-1 ring-black/5'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
              }`}
            >
              <Users2 className={`w-4 h-4 mr-2 ${activeTab === 'users' ? 'text-primary-500' : 'text-gray-400'}`} />
              Managers & Riders
            </button>
            <button
              onClick={() => setActiveTab('subscribers')}
              className={`flex-1 flex items-center justify-center px-6 py-3 rounded-xl text-sm font-bold transition-all duration-300 ${
                activeTab === 'subscribers'
                  ? 'bg-white text-primary-600 shadow-md transform scale-[1.02] ring-1 ring-black/5'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
              }`}
            >
              <UserPlus className={`w-4 h-4 mr-2 ${activeTab === 'subscribers' ? 'text-primary-500' : 'text-gray-400'}`} />
              Subscribers
              {userCounts?.subscribers > 0 && (
                <span className="ml-2 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full shadow-sm">
                  {userCounts.subscribers}
                </span>
              )}
            </button>
          </div>

          {activeTab === 'subscribers' ? (
            <Subscribers />
          ) : (
            <>

              {/* Search and Filters Section */}
              <div className="space-y-4">
                {/* Search Bar */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                  <div className="flex flex-col lg:flex-row gap-4 items-center">
                    {/* Search Input */}
                    <div className="flex-1 w-full">
                      <div className="relative">
                        <Search className={`absolute top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 ${isRTL ? 'right-4' : 'left-4'}`} />
                        <input
                          type="text"
                          placeholder={t('users.searchPlaceholder')}
                          value={searchTerm}
                          onChange={handleSearch}
                          className={`w-full py-3 px-10 text-base sm:text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ${isRTL ? 'pr-11 pl-4' : 'pl-11 pr-4'}`}
                        />
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-3 w-full lg:w-auto">
                      <button
                        onClick={clearFilters}
                        className="flex-1 lg:flex-none justify-center px-6 py-3 text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                      >
                        Clear
                      </button>

                      <button
                        onClick={fetchUsers}
                        className="flex-1 lg:flex-none flex justify-center items-center px-6 py-3 text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                      >
                        <RefreshCw className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Filter Controls */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                  <div className="flex flex-col gap-6">
                    {/* Status Filter Pills */}
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-gray-500 mr-1">Status:</span>
                      <button
                        onClick={() => { setStatusFilter(''); setCurrentPage(1); }}
                        className={`px-4 py-2 rounded-xl text-sm font-bold transition-all duration-200 ${
                          statusFilter === ''
                            ? 'bg-gray-900 text-white shadow-md'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        All
                      </button>
                      <button
                        onClick={() => { setStatusFilter('0'); setCurrentPage(1); }}
                        className={`px-4 py-2 rounded-xl text-sm font-bold transition-all duration-200 flex items-center gap-1.5 ${
                          statusFilter === '0'
                            ? 'bg-green-600 text-white shadow-md'
                            : 'bg-green-50 text-green-700 hover:bg-green-100'
                        }`}
                      >
                        <span className="w-2 h-2 rounded-full bg-current"></span>
                        Active Profiles
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                          statusFilter === '0' ? 'bg-green-500 text-white' : 'bg-green-200 text-green-800'
                        }`}>{activeCount}</span>
                      </button>
                      <button
                        onClick={() => { setStatusFilter('1'); setCurrentPage(1); }}
                        className={`px-4 py-2 rounded-xl text-sm font-bold transition-all duration-200 flex items-center gap-1.5 ${
                          statusFilter === '1'
                            ? 'bg-red-600 text-white shadow-md'
                            : 'bg-red-50 text-red-700 hover:bg-red-100'
                        }`}
                      >
                        <span className="w-2 h-2 rounded-full bg-current"></span>
                        Non-active Profiles
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                          statusFilter === '1' ? 'bg-red-500 text-white' : 'bg-red-200 text-red-800'
                        }`}>{deactivatedCount}</span>
                      </button>
                    </div>
                    {/* Controls Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* Role Dropdown */}
                      <div className="relative">
                        <select
                          value={roleFilter}
                          onChange={handleRoleFilter}
                          className="w-full appearance-none bg-white border border-gray-200 rounded-xl px-4 py-3 pr-10 text-sm font-semibold text-gray-700 hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                        >
                          <option value="">All Roles ({users.length})</option>
                          <option value="Admin">{t('users.personnel')} ({users.filter(u => u.role === 4 || u.role === 3).length})</option>
                          <option value="Coach">{t('users.coaches')} ({users.filter(u => u.role === 2).length})</option>
                          <option value="Rider">{t('users.riders')} ({users.filter(u => u.role === 0).length})</option>
                          <option value="Owner">{t('users.owners')} ({users.filter(u => u.role === 1).length})</option>
                          <option value="Temporary">{t('users.temporaryUsers')} ({users.filter(u => u.role === 5).length})</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                          <ChevronRight className="w-4 h-4 text-gray-400 transform rotate-90" />
                        </div>
                      </div>

                      {/* Age Group Filter */}
                      <div className="relative">
                        <select
                          value={ageGroupFilter}
                          onChange={(e) => setAgeGroupFilter(e.target.value)}
                          className="w-full appearance-none bg-white border border-gray-200 rounded-xl px-4 py-3 pr-10 text-sm font-semibold text-gray-700 hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                        >
                          <option value="">{t('demographics.allAges') || 'All Ages'}</option>
                          <option value="kids">{t('demographics.kids')}</option>
                          <option value="juniors">{t('demographics.juniors')}</option>
                          <option value="adults">{t('demographics.adults')}</option>
                          <option value="seniors">{t('demographics.seniors')}</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                          <ChevronRight className="w-4 h-4 text-gray-400 transform rotate-90" />
                        </div>
                      </div>

                      {/* Sort Order */}
                      <div className="relative">
                        <select
                          value={sortOrder}
                          onChange={(e) => setSortOrder(e.target.value)}
                          className="w-full appearance-none bg-white border border-gray-200 rounded-xl px-4 py-3 pr-10 text-sm font-semibold text-gray-700 hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                        >
                          <option value="name">Sort by Name</option>
                          <option value="age-asc">Age (Youngest First)</option>
                          <option value="age-desc">Age (Oldest First)</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                          <ChevronRight className="w-4 h-4 text-gray-400 transform rotate-90" />
                        </div>
                      </div>

                      {/* Add User Section */}
                      <div className="flex flex-col sm:flex-row items-stretch gap-2 w-full">
                        <div className="relative flex-1">
                          <select
                            value={selectedRoleForCreation}
                            onChange={(e) => setSelectedRoleForCreation(e.target.value)}
                            className="w-full appearance-none bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 pr-10 text-sm font-bold text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 h-full"
                          >
                            <option value="Rider">{t('users.addRider')}</option>
                            <option value="Owner">{t('users.addOwner')}</option>
                            <option value="Coach">{t('users.addCoach')}</option>
                            <option value="Temporary">{t('users.addTemporaryUser')}</option>
                          </select>
                          <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                            <ChevronRight className="w-4 h-4 text-blue-400 transform rotate-90" />
                          </div>
                        </div>

                        <button
                          onClick={() => setShowModal(true)}
                          className="bg-primary-600 text-white px-4 py-3 rounded-xl hover:bg-primary-700 transition-all duration-200 flex justify-center items-center font-bold shadow-sm active:scale-95 flex-shrink-0"
                          title={`Add ${selectedRoleForCreation}`}
                        >
                          <UserPlus className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Print and Export Buttons */}
                    <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4">
                      <button
                        onClick={handlePrintUsers}
                        className="flex-1 sm:flex-none justify-center bg-blue-600 text-white px-5 py-2.5 rounded-xl hover:bg-blue-700 transition-colors flex items-center text-sm font-medium shadow-sm active:scale-95"
                        title={t('users.printUsers')}
                      >
                        <Printer className="w-4 h-4 mr-2" />
                        Print
                      </button>
                      <button
                        onClick={handleExportUsers}
                        className="flex-1 sm:flex-none justify-center bg-green-600 text-white px-5 py-2.5 rounded-xl hover:bg-green-700 transition-colors flex items-center text-sm font-medium shadow-sm active:scale-95"
                        title={t('users.exportToCSV')}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Export
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {/* End of Fixed Header Section */}

              {/* Scrollable Cards Section */}
              <div className="px-4 sm:px-6 lg:px-8 pb-8">
                <div className="space-y-6">
                  {/* Select All and Users Cards */}
                  <div className="space-y-4">
                    {/* Select All Checkbox */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedUsers.length === users.length && users.length > 0}
                            onChange={handleSelectAll}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-3"
                          />
                          <span className="text-sm font-medium text-gray-700">
                            Select All ({selectedUsers.length} of {users.length} selected)
                          </span>
                        </div>
                        {selectedUsers.length > 0 && (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-500">
                              {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
                            </span>
                            <button className="px-3 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors">
                              Delete Selected
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Users Cards Grid */}
                    <div className="relative min-h-[200px]">
                      {loading && (
                        <div className="absolute inset-0 bg-white/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-xl">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                        </div>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
                      {users
                        .filter(user => {
                          if (!ageGroupFilter) return true;
                          if (!user.birthDate || user.birthDate === '0001-01-01T00:00:00') return false;
                          
                          const today = new Date();
                          const birth = new Date(user.birthDate);
                          let age = today.getFullYear() - birth.getFullYear();
                          const m = today.getMonth() - birth.getMonth();
                          if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
                            age--;
                          }

                          if (ageGroupFilter === 'kids') return age <= 12;
                          if (ageGroupFilter === 'juniors') return age > 12 && age <= 17;
                          if (ageGroupFilter === 'adults') return age > 17 && age <= 50;
                          if (ageGroupFilter === 'seniors') return age > 50;
                          return true;
                        })
                        .sort((a, b) => {
                          if (sortOrder === 'name') {
                            const nameA = (a.firstName + ' ' + a.lastName).toLowerCase();
                            const nameB = (b.firstName + ' ' + b.lastName).toLowerCase();
                            return nameA.localeCompare(nameB);
                          }
                          
                          const getAge = (birthDate) => {
                            if (!birthDate || birthDate === '0001-01-01T00:00:00') return -1;
                            const today = new Date();
                            const birth = new Date(birthDate);
                            let age = today.getFullYear() - birth.getFullYear();
                            const m = today.getMonth() - birth.getMonth();
                            if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
                              age--;
                            }
                            return age;
                          };

                          const ageA = getAge(a.birthDate);
                          const ageB = getAge(b.birthDate);

                          if (sortOrder === 'age-asc') return ageA - ageB;
                          if (sortOrder === 'age-desc') return ageB - ageA;
                          return 0;
                        })
                        .map((user) => (
                        <div key={user.id} className="bg-white rounded-xl shadow-md border border-gray-100 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 overflow-hidden group">
                          {/* Card Header with Gradient */}
                          <div className="relative h-20 bg-gradient-to-r from-blue-500 to-purple-600">
                            <div className="absolute inset-0 bg-black bg-opacity-10"></div>
                            <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
                              <input
                                type="checkbox"
                                checked={selectedUsers.includes(user.id)}
                                onChange={() => handleSelectUser(user.id)}
                                className="rounded border-white text-white focus:ring-white bg-white bg-opacity-20"
                              />
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold text-white shadow-lg ${getRoleBadgeColor(user.role)}`}>
                                {getRoleName(user.role)}
                              </span>
                            </div>
                          </div>

                          {/* User Photo */}
                          <div className="flex justify-center -mt-10 mb-3">
                            <div className="relative">
                              {getUserImageUrl(user) ? (
                                <img
                                  src={getUserImageUrl(user)}
                                  alt={user.name || 'User'}
                                  className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div className={`w-20 h-20 rounded-full bg-gradient-to-br from-blue-100 to-purple-200 flex items-center justify-center border-4 border-white shadow-lg ${getUserImageUrl(user) ? 'hidden' : 'flex'}`}>
                                <span className="text-2xl font-bold text-blue-600">
                                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                                </span>
                              </div>
                              {/* Online Status Indicator */}
                              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 border-2 border-white rounded-full flex items-center justify-center">
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                              </div>
                            </div>
                          </div>

                          {/* User Details */}
                          <div className="px-4 pb-3">
                            <div className="text-center">
                              <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors line-clamp-1">
                                {user.name || 'Unknown User'}
                              </h3>
                              <p className="text-xs text-gray-500 mb-2 truncate">{user.email}</p>

                              {/* Additional Info */}
                              <div className="space-y-2 text-xs text-gray-600">
                                {user.username && (
                                  <div className="flex items-center justify-center">
                                    <span className="font-mono bg-blue-50 text-blue-700 px-3 py-1 rounded-full border border-blue-200">
                                      @{user.username}
                                    </span>
                                  </div>
                                )}

                                <div className="grid grid-cols-2 gap-2 text-center">
                                  {user.birthDate && user.birthDate !== '0001-01-01T00:00:00' && (
                                    <div className="flex items-center justify-center bg-gray-50 rounded-lg p-2">
                                      <Calendar className="w-3 h-3 mr-1 text-blue-500" />
                                      <span className="font-medium">{new Date().getFullYear() - new Date(user.birthDate).getFullYear()}y</span>
                                    </div>
                                  )}

                                  {user.phoneNumber && (
                                    <div className="flex items-center justify-center bg-gray-50 rounded-lg p-2">
                                      <Phone className="w-3 h-3 mr-1 text-green-500" />
                                      <span className="font-medium truncate">{user.phoneNumber}</span>
                                    </div>
                                  )}
                                </div>

                                {user.address && (
                                  <div className="flex items-center justify-center bg-gray-50 rounded-lg p-2">
                                    <span className="truncate max-w-[200px] text-gray-700" title={user.address}>
                                      📍 {user.address}
                                    </span>
                                  </div>
                                )}

                                {user.bloodType && (
                                  <div className="flex items-center justify-center">
                                    <span className="bg-red-50 text-red-700 px-3 py-1 rounded-full border border-red-200 font-medium">
                                      🩸 {user.bloodType}
                                    </span>
                                  </div>
                                )}

                                {/* Rider Level - Only show for Riders */}
                                {user.role === 0 && user.level !== null && user.level !== undefined && (
                                  <div className="flex items-center justify-center">
                                    <span className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full border border-purple-200 font-medium">
                                      🏇 {getRiderLevelName(user.level)}
                                    </span>
                                  </div>
                                )}

                                {/* Subscription Status */}
                                {(user.role === 0 || user.role === 1) && (() => {
                                  const subscriptionStatus = getUserSubscriptionStatus(user.id);
                                  return subscriptionStatus ? (
                                    <div className="flex items-center justify-center">
                                      <span className={`px-3 py-1 rounded-full border font-medium text-xs ${getSubscriptionStatusColor(subscriptionStatus.status)}`}>
                                        📋 {subscriptionStatus.status}
                                        {subscriptionStatus.daysUntilExpiry !== undefined && (
                                          <span className="ml-1">
                                            ({subscriptionStatus.daysUntilExpiry > 0 ? `${subscriptionStatus.daysUntilExpiry}d left` : 'Expired'})
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  ) : null;
                                })()}
                              </div>

                              {/* Status and Password Change Notice */}
                              <div className="mt-4 space-y-2">
                                <div className="flex items-center justify-center space-x-2">
                                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${
                                    user.status === 0
                                      ? 'bg-green-100 text-green-800 border-green-200'
                                      : 'bg-red-100 text-red-800 border-red-200'
                                  }`}>
                                    {user.status === 0 ? '✓ Active' : '✕ Non-active'}
                                  </span>
                                  {user.mustChangePassword && (
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-orange-100 text-orange-800 border border-orange-200">
                                      🔒 Must change password
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Actions - Modern Icon Bar */}
                          <div className="bg-gray-50/50 px-4 py-3 border-t border-gray-100 flex items-center justify-between group-hover:bg-gray-50 transition-colors duration-300">
                            <button
                              onClick={() => handleViewUserDetails(user)}
                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                              title={t('users.viewDetails')}
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleEditUser(user)}
                              className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500/50"
                              title={t('users.editUser')}
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handlePrintBadge(user)}
                              className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                              title={t('common.printBadge')}
                            >
                              <CreditCard className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => {/* Delete user */ }}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500/50"
                              title={t('common.deleteUser')}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* End of Scrollable Cards Section */}

              {/* Pagination - Sticky at bottom */}
              <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
                  <div className="text-xs sm:text-sm text-gray-700 font-medium">
                    Showing <span className="font-bold text-blue-600">{((currentPage - 1) * itemsPerPage) + 1}</span> to <span className="font-bold text-blue-600">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of <span className="font-bold text-blue-600">{totalItems}</span> results
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="flex items-center px-3 py-2 sm:px-4 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-all duration-200 min-h-[44px] touch-manipulation"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">Previous</span>
                      <span className="sm:hidden">Prev</span>
                    </button>

                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                        if (pageNum > totalPages) return null;

                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 min-h-[44px] min-w-[44px] touch-manipulation ${currentPage === pageNum
                                ? 'bg-blue-600 text-white shadow-lg'
                                : 'text-gray-700 hover:bg-gray-100 border border-gray-300'
                              }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="flex items-center px-3 py-2 sm:px-4 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-all duration-200 min-h-[44px] touch-manipulation"
                    >
                      <span className="hidden sm:inline">Next</span>
                      <span className="sm:hidden">Next</span>
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Modals */}
              <UserCreationModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                onUserCreated={handleUserCreated}
                role={selectedRoleForCreation}
              />

              {editingUser && (
                <UserEditModal
                  isOpen={showEditModal}
                  onClose={() => {
                    setShowEditModal(false);
                    setEditingUser(null);
                  }}
                  onUserUpdated={handleUserUpdated}
                  user={editingUser}
                />
              )}

              {viewingUser && (
                <UserDetailsModal
                  isOpen={showDetailsModal}
                  onClose={() => {
                    setShowDetailsModal(false);
                    setViewingUser(null);
                  }}
                  user={viewingUser}
                />
              )}
            </>
          )}
        </div>
        {/* End of Fixed Header Section */}
      </div>
      {/* End of main flex container */}
    </>
  );
}

export default Users;
